/**
 * Vue 自定义指令：v-permission
 * 用于根据权限控制元素的显示和隐藏
 * 
 * 使用方式：
 * 1. 单个权限：
 *    <el-button v-permission="'user:add'">添加用户</el-button>
 * 
 * 2. 多个权限（OR 逻辑，拥有任意一个即可）：
 *    <el-button v-permission="['user:edit', 'user:delete']">操作</el-button>
 * 
 * 3. 多个权限（AND 逻辑，必须全部拥有）：
 *    <el-button v-permission.all="['user:edit', 'user:delete']">批量操作</el-button>
 * 
 * 4. 角色检查：
 *    <el-button v-permission.role="'admin'">管理员功能</el-button>
 */

import { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } from '@/utils/permission';

export default {
    /**
     * 指令插入到 DOM 时调用
     */
    inserted(el, binding) {
        const { value, modifiers } = binding;
        
        if (!value) {
            // 没有传值，默认显示
            return;
        }
        
        let hasAuth = false;
        
        // 检查修饰符
        if (modifiers.role) {
            // v-permission.role：角色检查
            if (typeof value === 'string') {
                hasAuth = hasRole(value);
            } else if (Array.isArray(value)) {
                hasAuth = value.some(role => hasRole(role));
            }
        } else if (modifiers.all) {
            // v-permission.all：必须拥有所有权限（AND 逻辑）
            if (Array.isArray(value)) {
                hasAuth = hasAllPermissions(value);
            } else {
                hasAuth = hasPermission(value);
            }
        } else {
            // 默认：单个权限或拥有任意一个权限即可（OR 逻辑）
            if (Array.isArray(value)) {
                hasAuth = hasAnyPermission(value);
            } else if (typeof value === 'string') {
                hasAuth = hasPermission(value);
            }
        }
        
        if (!hasAuth) {
            // 没有权限，移除元素
            el.parentNode && el.parentNode.removeChild(el);
        }
    },
    
    /**
     * 组件更新时调用（可选）
     * 当权限动态变化时，重新检查权限
     */
    update(el, binding) {
        const { value, modifiers, oldValue } = binding;
        
        // 如果权限要求没有变化，不需要重新检查
        if (JSON.stringify(value) === JSON.stringify(oldValue)) {
            return;
        }
        
        if (!value) {
            return;
        }
        
        let hasAuth = false;
        
        if (modifiers.role) {
            if (typeof value === 'string') {
                hasAuth = hasRole(value);
            } else if (Array.isArray(value)) {
                hasAuth = value.some(role => hasRole(role));
            }
        } else if (modifiers.all) {
            if (Array.isArray(value)) {
                hasAuth = hasAllPermissions(value);
            } else {
                hasAuth = hasPermission(value);
            }
        } else {
            if (Array.isArray(value)) {
                hasAuth = hasAnyPermission(value);
            } else if (typeof value === 'string') {
                hasAuth = hasPermission(value);
            }
        }
        
        if (!hasAuth) {
            // 没有权限，隐藏元素（使用 display: none）
            el.style.display = 'none';
        } else {
            // 有权限，显示元素
            el.style.display = '';
        }
    }
};

